import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * [MOTION-B 2026-08-20] "Did that save?" — answered without a toast.
 *
 * A save button that only swaps its label to "Saving…" and then back leaves the user
 * inferring success from the absence of an error. This gives the button three states —
 * idle → spinner → tick — and holds the tick briefly so the confirmation is actually seen.
 *
 * WHERE THIS BELONGS. Only on saves whose button STAYS ON SCREEN afterwards (the Sales
 * Scoreboard tier levers, in-place edits). For a save that CLOSES its modal — Edit Mapping,
 * Add Payment, Create Deal — holding a tick would mean holding the modal open for an extra
 * beat purely to show it, i.e. deliberately slowing a manager down after they clicked Save.
 * Those flows confirm through the success toast plus the saved-row bloom in the table
 * (DataTable's `rowFlashFn`), which point at the changed data instead of at the button.
 *
 * The spinner is a border trick — one composited rotation, no SVG, no library.
 */

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// The tick draws itself: the stroke is dashed with a dash as long as the path, then the
// offset is animated to zero. Cheaper and calmer than scaling an icon in.
const draw = keyframes`
  to { stroke-dashoffset: 0; }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 620ms linear infinite;
  /* Reduced motion: a spinner conveys "working" only by spinning, so it is left running —
     the global reduced-motion rule caps duration, which would freeze it. Kept explicit. */
`;

const Tick = styled.svg`
  width: 13px;
  height: 13px;
  display: inline-block;
  vertical-align: -2px;

  path {
    fill: none;
    stroke: currentColor;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 18;
    stroke-dashoffset: 18;
    animation: ${draw} 220ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    path { animation: none; stroke-dashoffset: 0; }
  }
`;

const Inline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  /* Fixes the button's width across all three states so it never jitters mid-save. */
  justify-content: center;
`;

export type SavePhase = 'idle' | 'saving' | 'done';

/** The button's contents for the current phase. `label` is the idle text. */
export const SaveButtonContent: React.FC<{
  phase: SavePhase;
  label: React.ReactNode;
  savingLabel?: string;
  doneLabel?: string;
}> = ({ phase, label, savingLabel = 'Saving…', doneLabel = 'Saved' }) => {
  if (phase === 'saving') {
    return <Inline><Spinner aria-hidden="true" />{savingLabel}</Inline>;
  }
  if (phase === 'done') {
    return (
      <Inline>
        <Tick viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3.2L13 5" /></Tick>
        {doneLabel}
      </Inline>
    );
  }
  return <>{label}</>;
};

/**
 * Drives the three phases around an async save.
 *
 * `run(fn)` → spinner while `fn` runs → tick for `holdMs` → back to idle. A FAILED save
 * returns to idle immediately and rethrows, so the caller's existing error handling (its
 * toast, keeping a form open) is untouched and a failure can never show a tick.
 */
export function useSavePhase(holdMs = 900): {
  phase: SavePhase;
  run: <T>(fn: () => Promise<T>) => Promise<T>;
} {
  const [phase, setPhase] = useState<SavePhase>('idle');
  const timer = useRef<number | null>(null);
  const alive = useRef(true);

  useEffect(() => () => {
    alive.current = false;
    if (timer.current != null) window.clearTimeout(timer.current);
  }, []);

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    if (timer.current != null) { window.clearTimeout(timer.current); timer.current = null; }
    setPhase('saving');
    try {
      const out = await fn();
      if (alive.current) {
        setPhase('done');
        timer.current = window.setTimeout(() => {
          if (alive.current) setPhase('idle');
          timer.current = null;
        }, holdMs);
      }
      return out;
    } catch (err) {
      if (alive.current) setPhase('idle');
      throw err;
    }
  }, [holdMs]);

  return { phase, run };
}

export default SaveButtonContent;
